import React from 'react';
import {IconButton} from '@mui/material';
import {
  Button,
  Icon,
  designSystemToken,
  Headline,
  Label,
  Tooltip,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type {Segment} from '@/types/models/segments/segment';
import {
  getChipLabel,
  getItemKey,
} from '@/views/creators/creator-crm/utils/filterChipUtils';
import Chip from '@/components/chip';
import styles from './ActiveFiltersBar.module.scss';
import type {DisplayItem, GroupedDisplayItems} from './displayItemTypes';

export type ActiveFiltersBarProps = {
  /** Grouped display items (search chips and/or filter chips) to show. */
  groupedDisplayItems: GroupedDisplayItems[];
  /** When set, show "Active Segment" label and segment name chip. */
  activeSegment?: Segment | null;
  /** When provided, chips are removable; when omitted, display-only. */
  onRemoveItem?: (item: DisplayItem) => void;
  /** When provided and no active segment, show a save-filters bookmark button. */
  onSaveFilters?: () => void;
  /** When provided, show a "Clear all" button at the end of the chips. */
  onClearAll?: () => void;
};

function ActiveFiltersBar({
  groupedDisplayItems,
  activeSegment = null,
  onRemoveItem,
  onSaveFilters,
  onClearAll,
}: ActiveFiltersBarProps) {
  const hasItems = groupedDisplayItems.length > 0;
  const hasSegment = activeSegment != null;
  if (!hasItems && !hasSegment) {
    return null;
  }

  const label = hasSegment
    ? translate('Active saved filter')
    : translate('Active filters');

  const showSaveButton = !hasSegment && onSaveFilters != null;

  return (
    <div className={styles.container}>
      <Headline size="xxs" color={designSystemToken('semantic.fg.secondary')}>
        {label}:
      </Headline>
      {showSaveButton ? (
        <Tooltip
          title={translate('Save Filters')}
          placement="top"
          type="dark"
          arrow
        >
          <IconButton
            className={styles.saveButton}
            onClick={onSaveFilters}
            aria-label={translate('Save Filters')}
          >
            <Icon
              name="Actions-Bookmark"
              size="small"
              appearance="neutral"
              color={designSystemToken('semantic.fg.secondary')}
              style={{width: '20px', height: '20px'}}
            />
          </IconButton>
        </Tooltip>
      ) : null}
      {hasSegment && (
        <div className={styles.activeSegment}>
          <Chip
            label={activeSegment.name}
            size="small"
            icon={
              <Icon
                name="Actions-Bookmark"
                size="small"
                appearance="brand"
                color={designSystemToken('semantic.fg.brand')}
                style={{width: '16px', height: '16px'}}
              />
            }
            variant="outlined"
            sx={{
              'borderColor': `${designSystemToken('semantic.fg.brand')} !important`,
              'color': designSystemToken('semantic.fg.brand'),
              'fontWeight': 500,
              'borderRadius': '18px',
              'height': '24px',
              'fontSize': '14px',
              '& .MuiChip-icon': {
                marginLeft: '8px',
                marginRight: '-2px',
              },
              '& .MuiChip-label': {
                paddingLeft: '6px',
                paddingRight: '12px',
              },
            }}
          />
        </div>
      )}
      {groupedDisplayItems.flatMap(({items}) =>
        items.map((item) => (
          <Chip
            key={getItemKey(item)}
            label={getChipLabel(item)}
            sx={{borderColor: 'transparent !important'}}
            {...(onRemoveItem ? {handleDelete: () => onRemoveItem(item)} : {})}
            size="small"
          />
        ))
      )}
      {onClearAll ? (
        <Button
          size="small"
          variant="text"
          className={styles.clearButton}
          onClick={onClearAll}
          sx={{height: '24px'}}
        >
          <Label size="sm">{translate('Clear All')}</Label>
        </Button>
      ) : null}
    </div>
  );
}

export default ActiveFiltersBar;
